/*
Product Form — Subscription Bundle variant

Isolated copy of the ProductForm class from web-components-product.js,
registered under its own custom element tag so it never touches the
shared product-form behavior used elsewhere in the theme.

The only functional change from the original is the purchase_option
selector fix in initAddToCart() below (see comment at that line).
*/

class ProductFormSubscriptionBundle extends HTMLElement {
  connectedCallback() {
    try {
      this.variants = JSON.parse(this.dataset.variants || "[]");
      this.sellingPlanGroups = JSON.parse(this.dataset.sellingPlans || "[]");
      this.productOptions = JSON.parse(this.dataset["productOptions"]);
      this.defaultPurchaseOption = this.dataset.defaultPurchaseOption;
      this.subscriptionProduct = JSON.parse(
        this.dataset.subscriptionProduct || "null"
      );
      this.discountCents = this.dataset.discountCents
        ? parseInt(this.dataset.discountCents, 10)
        : 0;
      this.subscriptionMonthlyPriceCents = this.dataset.subscriptionMonthlyPrice
        ? parseInt(this.dataset.subscriptionMonthlyPrice, 10)
        : 0;
      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
    } catch (e) {
      console.error("Invalid dataset", e);
      return;
    }

    // Custom elements upgrade — and connectedCallback fires — the instant
    // the parser inserts their opening tag, before it has parsed this
    // element's own light-DOM children (the radios and price nodes inside
    // <form> below). On a page this size the parser can yield back to the
    // event loop mid-document, so a fixed-delay retry (this used to be a
    // setTimeout(0) here) isn't reliable: it can fire during one of those
    // yields, after some siblings exist but before others do — e.g. the
    // price display parsed but the purchase_option radios not yet, so
    // reading ":checked" comes up empty and the price falls back to the
    // one-time amount. Wait for the parser to actually finish instead of
    // guessing at a delay; readyState is only ever "loading" while it's
    // still running.
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.init(), {
        once: true,
      });
    } else {
      this.init();
    }
  }

  init() {
    this.initSelectedVariantFromUrl();
    this.initOptionListeners();
    this.initQuantityListeners();
    this.initAddToCart();

    // Ensure the default purchase option is actually selected in the DOM
    if (this.defaultPurchaseOption === "subscribe") {
      const subscribeInput = this.querySelector(
        'input[name^="purchase_option_"][value="subscribe"]'
      );
      if (subscribeInput) {
        subscribeInput.checked = true;
      }
    }

    // This will now see the correct purchase option and sync everything
    this.handleOptionChange();

    // Explicitly ensure hidden inputs and prices are correct on load
    this.updateHiddenInputs();
    this.updatePrices();
  }

  initAddToCart() {
    // Delegated on `this` for the same reason as initOptionListeners(): the
    // <form> is a Liquid-rendered child that may not exist yet when
    // connectedCallback runs, so binding directly to it (found via
    // querySelector at that moment) could silently attach nothing.
    this.addEventListener("submit", async (e) => {
      const form = e.target;
      if (form.tagName !== "FORM") return;
      const atcButton = form.querySelector("button[type='submit']");
      if (!atcButton) return;

      e.preventDefault();
      atcButton.disabled = true;
      atcButton.innerHTML = "Adding...";

      const variantInput = this.querySelector('input[name="id"]');
      const quantityInput = this.querySelector('input[name="quantity"]');
      const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
      const subscriptionIdInput = this.querySelector(
        'input[name="subscription_id"]'
      );
      const subscriptionSellingPlanInput = this.querySelector(
        'input[name="subscription_selling_plan"]'
      );
      // Fixed: the real radios are named purchase_option_{{ group }}, not
      // the literal "purchase_option" — matches the selector used
      // everywhere else in this file (updateHiddenInputs, updatePrices,
      // initOptionListeners, handleOptionChange).
      const purchaseOption = this.querySelector(
        'input[name^="purchase_option_"]:checked'
      )?.value;

      if (!variantInput || !quantityInput) return;

      const quantity = parseInt(quantityInput.value, 10);
      const items = [];

      items.push({
        id: variantInput.value,
        quantity: quantity,
        ...(sellingPlanInput?.value
          ? { selling_plan: sellingPlanInput.value }
          : {}),
      });

      if (
        purchaseOption === "subscribe" &&
        subscriptionIdInput?.value &&
        subscriptionSellingPlanInput?.value
      ) {
        items.push({
          id: subscriptionIdInput.value,
          quantity: quantity,
          selling_plan: subscriptionSellingPlanInput.value,
        });
      }

      try {
        // /cart/add.js merges into whatever is already in the cart, so a
        // Feno Membership left behind from an earlier visit (e.g. the
        // shopper removed the bundle but kept the membership, then came
        // back and clicked Buy Now again) would stack with the one added
        // below and they'd end up with two memberships for one bundle.
        // Clear any existing membership lines first. Only memberships:
        // other cart lines, including a leftover bundle, are left alone.
        if (purchaseOption === "subscribe" && this.subscriptionProduct) {
          const membershipVariantIds = new Set(
            this.subscriptionProduct.variants.map((v) => v.id)
          );
          const cartResponse = await fetch("/cart.js");
          if (!cartResponse.ok)
            throw new Error(`Cart fetch failed: ${cartResponse.statusText}`);
          const cart = await cartResponse.json();
          const updates = {};
          cart.items.forEach((line) => {
            if (membershipVariantIds.has(line.variant_id)) {
              updates[line.key] = 0;
            }
          });
          if (Object.keys(updates).length) {
            const clearResponse = await fetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ updates }),
            });
            if (!clearResponse.ok)
              throw new Error(
                `Clearing memberships failed: ${clearResponse.statusText}`
              );
          }
        }

        for (const item of items) {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items: [item] }),
          });

          if (!response.ok)
            throw new Error(`Add to cart failed: ${response.statusText}`);
        }

        // TVSquared action tracking
        if (window.tvqTrackAction) {
          var productTitle = document.querySelector('h1')?.textContent?.trim() || '';
          var price = String((this.selectedVariant?.price || 0) / 100);

          window.tvqTrackAction('add_to_cart', {
            'rev': '0', 'prod': productTitle, 'id': String(variantInput.value), 'promo': ''
          });
          window.tvqTrackAction('checkout_started', {
            'rev': price, 'prod': productTitle, 'id': '', 'promo': ''
          });
          if (purchaseOption === "subscribe") {
            window.tvqTrackAction('subscription_signup', {
              'rev': price, 'prod': productTitle, 'id': String(variantInput.value), 'promo': ''
            });
          }
        }

        // The "Subscribe & Save" price is enforced by an automatic discount
        // (a BXGY discount keyed to the Feno Membership being purchased as a
        // subscription — see the comment in
        // main-product-subscription-bundle.liquid). It applies on its own
        // once both lines are in the cart, so both purchase options go
        // straight to checkout. (It replaced the FENOMEM100 code, which had
        // to be applied here via a /discount/FENOMEM100 redirect.)
        window.location.href = "/checkout";
      } catch (err) {
        console.error("Error adding to cart", err);
        alert("Something went wrong while adding to cart. Please try again.");
        atcButton.disabled = false;
        atcButton.innerHTML = "Buy Now";
      }
    });
  }

  initQuantityListeners() {
    const quantityEl = this.querySelector(".quantity-js");
    if (!quantityEl) return;
    const plusBtn = this.querySelector(".quantity-plus-js");
    const minusBtn = this.querySelector(".quantity-minus-js");

    if (plusBtn) {
      plusBtn.addEventListener("click", () => {
        let quantity = parseInt(quantityEl.textContent.trim()) || 1;
        quantity++;
        quantityEl.textContent = quantity;
        this.updateQuantityInput(quantity);
      });
    }

    if (minusBtn) {
      minusBtn.addEventListener("click", () => {
        let quantity = parseInt(quantityEl.textContent.trim()) || 1;
        quantity = Math.max(1, quantity - 1);
        quantityEl.textContent = quantity;
        this.updateQuantityInput(quantity);
      });
    }
  }

  updateQuantityInput(quantity) {
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (quantityInput) quantityInput.value = quantity;
  }

  // Formats cents with the storefront's money format, which Shopify Markets
  // localizes per country — so it can use any of Shopify's amount tokens, not
  // just {{amount}}. Handling only a few left the others on the page verbatim
  // (Sweden's "{{amount_with_space_separator}} kr" rendered as-is). Each
  // token's separators are fixed, as in Shopify's own Liquid money filters,
  // rather than taken from the browser's locale.
  formatMoney(cents) {
    const format = (decimals, thousands, decimal) => {
      const [whole, fraction] = ((cents || 0) / 100).toFixed(decimals).split(".");
      // Drop ".00" like Liquid's money_without_trailing_zeros; keep real cents.
      return (
        whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) +
        (fraction && Number(fraction) !== 0 ? decimal + fraction : "")
      );
    };
    const tokens = {
      amount: () => format(2, ",", "."),
      amount_no_decimals: () => format(0, ",", "."),
      amount_with_comma_separator: () => format(2, ".", ","),
      amount_no_decimals_with_comma_separator: () => format(0, ".", ","),
      amount_with_apostrophe_separator: () => format(2, "'", "."),
      amount_no_decimals_with_space_separator: () => format(0, " ", ","),
      amount_with_space_separator: () => format(2, " ", ","),
      amount_with_period_and_space_separator: () => format(2, " ", "."),
    };
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) =>
      (tokens[token] || tokens.amount)()
    );
  }

  computeDueTodayPriceCents() {
    const subscribeBundlePriceCents =
      (this.selectedVariant?.price || 0) - this.discountCents;
    const dueTodayExactCents =
      subscribeBundlePriceCents + this.subscriptionMonthlyPriceCents;
    return Math.floor(dueTodayExactCents / 100) * 100;
  }

  updatePrices() {
    const onetimePriceEl = this.querySelector("#onetime-price");
    if (onetimePriceEl && this.selectedVariant) {
      onetimePriceEl.textContent = this.formatMoney(this.selectedVariant.price);
    }

    // "Due today" is the discounted bundle price (selected variant's price
    // minus the subscribe discount) plus the subscription's first month,
    // floored to a whole dollar -- same math as due_today_price in
    // pdp-block-subscription-bundle.liquid, redone here against whichever
    // variant is currently selected instead of the one rendered at page load.
    const dueTodayPriceEl = this.querySelector("#due-today-price");
    if (dueTodayPriceEl && this.subscriptionProduct && this.selectedVariant) {
      dueTodayPriceEl.textContent = this.formatMoney(
        this.computeDueTodayPriceCents()
      );
    }

    const subscriptionPriceEl = this.querySelector("#subscription-price");
    const subscriptionDiscountEl = this.querySelector("#subscription-discount");
    const subscriptionLabel = this.querySelector("#subscription-label");
    const planSelector = this.querySelector("#subscription-plan");

    const purchaseOption = this.querySelector(
      'input[name^="purchase_option_"]:checked'
    )?.value;

    let activePriceCents = this.selectedVariant.price;

    if (!this.subscriptionProduct && purchaseOption === "subscribe") {
      const selectedPlanId = parseInt(planSelector.value);
      const allocation = this.selectedVariant.selling_plan_allocations?.find(
        (a) => a.selling_plan_id == selectedPlanId
      );
      if (allocation) {
        activePriceCents = allocation.price_adjustments[0]?.price;
      }
    } else if (this.subscriptionProduct && purchaseOption === "subscribe") {
      // Force-add-a-subscription case: the discounted "subscribe" price
      // isn't a native selling plan allocation, it's the selected
      // variant's price combined with the subscribe discount and the
      // subscription's first month (see computeDueTodayPriceCents).
      activePriceCents = this.computeDueTodayPriceCents();
    }

    const formattedActivePrice = this.formatMoney(activePriceCents);

    document.querySelectorAll(".price-js").forEach((el) => {
      el.textContent = formattedActivePrice;
    });

    // Keeps the "Buy Now - $X" button label matching whichever purchase
    // option (subscribe vs. one-time) is currently selected.
    this.querySelectorAll(".buy-btn-price-js").forEach((el) => {
      el.textContent = formattedActivePrice;
    });

    if (!planSelector || !subscriptionPriceEl) return;

    if (this.subscriptionProduct) {
      let subVariant = this.subscriptionProduct.variants[0];
      if (planSelector.value) {
        const matchingVariant = this.subscriptionProduct.variants.find(
          (variant) =>
            variant.selling_plan_allocations?.some(
              (allocation) => allocation.selling_plan_id == planSelector.value
            )
        );
        if (matchingVariant) subVariant = matchingVariant;
      }

      subscriptionPriceEl.innerHTML = `<s class="opacity-50">${this.formatMoney(subVariant.price)}</s> <span>${this.formatMoney(0)}</span>`;

      if (subscriptionLabel) {
        subscriptionLabel.textContent = "Subscribe";
      }
    } else if (this.selectedVariant?.selling_plan_allocations?.length > 0) {
      const availablePlans = this.selectedVariant.selling_plan_allocations.map(
        (alloc) => alloc.selling_plan_id
      );

      [...planSelector.options].forEach((opt) => {
        if (!availablePlans.includes(parseInt(opt.value))) {
          opt.style.display = "none";
        } else {
          opt.style.display = "";
        }
      });

      if (!availablePlans.includes(parseInt(planSelector.value))) {
        planSelector.value = availablePlans[0];
      }

      this.updateSubscriptionPlanDisplay();

      const selectedPlanId = parseInt(planSelector.value);
      const allocation = this.selectedVariant.selling_plan_allocations.find(
        (a) => a.selling_plan_id == selectedPlanId
      );

      if (allocation) {
        const fullPrice = this.selectedVariant.price / 100;
        const discountedPrice = allocation.price_adjustments[0]?.price / 100;
        const discountAmount = fullPrice - discountedPrice;

        if (discountAmount > 0) {
          subscriptionPriceEl.innerHTML = `<s class="opacity-50">${this.formatMoney(fullPrice * 100)}</s> <span>${this.formatMoney(discountedPrice * 100)}</span>`;
          if (subscriptionLabel) {
            subscriptionLabel.textContent = `Subscribe & Save ${this.formatMoney(discountAmount * 100)}`;
          }
        } else {
          subscriptionPriceEl.innerHTML = this.formatMoney(discountedPrice * 100);
          if (subscriptionLabel) {
            subscriptionLabel.textContent = `Subscribe`;
          }
        }
      }
    }
  }

  initSelectedVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = parseInt(urlParams.get("variant"), 10);
    if (variantId && this.variants.some((v) => v.id === variantId)) {
      this.selectedVariant = this.variants.find((v) => v.id === variantId);
    } else {
      this.selectedVariant =
        this.variants.find((v) => v.available) || this.variants[0];
      this.updateUrlWithVariant(this.selectedVariant.id);
    }
  }

  initOptionListeners() {
    // Delegated on `this` rather than bound directly to the radios/select:
    // connectedCallback can fire before these Liquid-rendered children
    // (nested several levels down in a block loop) have actually been
    // inserted into the DOM yet, which made direct querySelectorAll+
    // addEventListener binding silently attach to nothing. Delegation only
    // needs `this` to exist, so it's immune to that timing gap.
    this.addEventListener("change", (e) => {
      const target = e.target;

      if (target.matches('input[type="radio"][name^="option-"]')) {
        this.handleOptionChange();
      } else if (
        target.matches('input[name^="purchase_option_"]') ||
        target.matches("#subscription-plan")
      ) {
        this.updateHiddenInputs();
        this.updatePrices();
        this.updateSubscriptionPlanDisplay();
      }
    });
  }

  updateSubscriptionPlanDisplay() {
    const planSelector = this.querySelector("#subscription-plan");
    const planDisplay = this.querySelector("#subscription-plan-display");
    if (!planSelector || !planDisplay) return;

    const selectedOption = planSelector.options[planSelector.selectedIndex];
    if (selectedOption) {
      planDisplay.textContent = selectedOption.textContent;
    }
  }

  handleOptionChange() {
    const labelEl = this.querySelector(".variant-label-js");
    const selectedOptions = [];
    const optionGroups = [
      ...new Set(
        [...this.querySelectorAll('input[type="radio"][name^="option-"]')].map(
          (input) => input.name
        )
      ),
    ];

    optionGroups.forEach((groupName) => {
      const selectedInput = this.querySelector(
        `input[name="${groupName}"]:checked`
      );
      if (selectedInput) selectedOptions.push(selectedInput.value);
    });

    const matchingVariant = this.variants.find((variant) => {
      return variant.options.every((opt, idx) => opt === selectedOptions[idx]);
    });

    if (matchingVariant) {
      this.selectedVariant = matchingVariant;
      this.updateUrlWithVariant(matchingVariant.id);
      this.updateActiveStates();

      const planSelector = this.querySelector("#subscription-plan");
      if (planSelector && !this.subscriptionProduct) {
        const availablePlans =
          this.selectedVariant.selling_plan_allocations.map(
            (alloc) => alloc.selling_plan_id
          );

        [...planSelector.options].forEach((opt) => {
          if (!availablePlans.includes(parseInt(opt.value))) {
            opt.style.display = "none";
          } else {
            opt.style.display = "";
          }
        });

        if (!availablePlans.includes(parseInt(planSelector.value))) {
          planSelector.value = availablePlans[0];
        }
      }
    }

    // Always sync hidden inputs and prices, even if variant did not change
    this.updateHiddenInputs();
    this.updatePrices();

    if (labelEl) {
      const colorOption = this.productOptions?.find(
        (option) => option.name === "Color"
      );

      if (!!colorOption) {
        labelEl.textContent = this.selectedVariant.title;
      }
    }
  }

  updateHiddenInputs() {
    const purchaseOption = this.querySelector(
      'input[name^="purchase_option_"]:checked'
    );

    const variantInput = this.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = this.selectedVariant?.id || "";
    }

    const sellingPlanInput = this.querySelector('input[name="selling_plan"]');
    const subscriptionIdInput = this.querySelector(
      'input[name="subscription_id"]'
    );
    const subscriptionSellingPlanInput = this.querySelector(
      'input[name="subscription_selling_plan"]'
    );
    const planSelector = this.querySelector("#subscription-plan");

    if (purchaseOption?.value === "subscribe") {
      if (this.subscriptionProduct) {
        const selectedSellingPlanId = planSelector.value;
        const matchingVariant = this.subscriptionProduct.variants.find(
          (variant) =>
            variant.selling_plan_allocations?.some(
              (allocation) =>
                allocation.selling_plan_id == selectedSellingPlanId
            )
        );

        if (subscriptionIdInput && matchingVariant) {
          subscriptionIdInput.value = matchingVariant.id;
        } else if (subscriptionIdInput) {
          subscriptionIdInput.value = "";
        }

        if (subscriptionSellingPlanInput) {
          subscriptionSellingPlanInput.value = selectedSellingPlanId;
        }

        if (sellingPlanInput) {
          sellingPlanInput.value = "";
        }
      } else {
        if (planSelector && sellingPlanInput) {
          const selectedSellingPlanId = parseInt(planSelector.value);
          const allocation =
            this.selectedVariant.selling_plan_allocations?.find(
              (alloc) => alloc.selling_plan_id == selectedSellingPlanId
            );
          if (allocation) {
            sellingPlanInput.value = selectedSellingPlanId;
          } else {
            sellingPlanInput.value = "";
          }
        }

        if (subscriptionIdInput) {
          subscriptionIdInput.value = "";
        }
        if (subscriptionSellingPlanInput) {
          subscriptionSellingPlanInput.value = "";
        }
      }
    } else {
      if (sellingPlanInput) {
        sellingPlanInput.value = "";
      }
      if (subscriptionIdInput) {
        subscriptionIdInput.value = "";
      }
      if (subscriptionSellingPlanInput) {
        subscriptionSellingPlanInput.value = "";
      }
    }
  }

  updateActiveStates() {
    this.querySelectorAll(".option").forEach((el) =>
      el.classList.remove("active")
    );
    const optionGroups = [
      ...new Set(
        [...this.querySelectorAll('input[type="radio"][name^="option-"]')].map(
          (input) => input.name
        )
      ),
    ];
    optionGroups.forEach((groupName) => {
      const selectedInput = this.querySelector(
        `input[name="${groupName}"]:checked`
      );
      if (selectedInput)
        selectedInput.previousElementSibling?.classList.add("active");
    });
  }

  updateUrlWithVariant(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant", variantId);
    // replaceState, not pushState: picking a variant (including the one the
    // page selects on load) must not add a history entry, or Back takes two
    // presses to leave the product page.
    window.history.replaceState({}, "", url.toString());
  }
}

customElements.define(
  "product-form-subscription-bundle",
  ProductFormSubscriptionBundle
);
